// Cortex OS — Approvals (client Action Center).
//
// Clients see ONLY the decisions that require their approval. Decisions
// travel exclusively through /decide (the exactly-once continuation
// engine): the page never invents decision options (they come from the
// packet contract), never falls back to legacy endpoints, never marks
// anything complete before the workflow verified, and treats replays and
// races as informative resolutions. Arriving via a signed email link
// (?approvalToken=) resolves read-only first; deciding always requires
// this authenticated session.

import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  formatEstimate,
  decisionLabel,
  interpretDecideResponse,
  continuationStateLabel,
} from "../utils/actionCenterPresentation";
import "./ClientApprovals.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ?? "";

// The Action Center backend (/api/action-center/*, the exactly-once /decide
// engine) is NOT mounted on backend main yet — it ships with the trust-center
// work. Until it does, this surface must not fire requests that can only 404.
// Flip this single constant when the backend routes are live; the
// cross-repository route-contract test enforces that this gate exists while
// the routes are absent, and fails if the routes mount while this stays off.
const ACTION_CENTER_BACKEND_MOUNTED = false;
const ACTION_CENTER_DISABLED_NOTE =
  "Approvals are not enabled yet. Your Cphere team will contact you directly for any decision that needs you.";

function authHeaders() {
  const token =
    localStorage.getItem("clientAuthToken") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Client surfaces never expose internal operator identities.
function clientSafeMessage(text) {
  return String(text || "")
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "your Cphere team")
    .replace(/user:\d+/g, "your Cphere team");
}

const CHANGE_SECTIONS = ["Budget", "Creative", "Timeline", "Audience", "Other"];

export default function ClientApprovals() {
  const location = useLocation();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [packets, setPackets] = useState({});
  const [outcome, setOutcome] = useState(null); // {actionId, tone, label, detail}
  const [linkStatus, setLinkStatus] = useState(null);
  const [changesFor, setChangesFor] = useState(null); // actionId with open form
  const [changeForm, setChangeForm] = useState({
    section: CHANGE_SECTIONS[0],
    change: "",
    reason: "",
    blocking: true,
  });

  const loadSummary = useCallback(async () => {
    if (!ACTION_CENTER_BACKEND_MOUNTED) return;
    try {
      const response = await fetch(`${API_BASE_URL}/api/action-center/summary`, {
        headers: authHeaders(),
        cache: "no-store",
      });
      if (!response.ok) throw new Error(`Approvals failed to load (${response.status})`);
      setSummary(await response.json());
    } catch (loadError) {
      setError(loadError?.message || "Approvals failed to load.");
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Signed-link arrival: resolve read-only; the link itself never decides.
  useEffect(() => {
    if (!ACTION_CENTER_BACKEND_MOUNTED) return;
    const params = new URLSearchParams(location.search);
    const token = params.get("approvalToken");
    if (!token) return;
    (async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/action-center/approval-link/${encodeURIComponent(token)}`,
          { headers: authHeaders(), cache: "no-store" }
        );
        const payload = await response.json();
        setLinkStatus({
          status: payload.status,
          label: continuationStateLabel(
            payload.status === "completed" ? "completed_elsewhere" : payload.status
          ),
          detail: clientSafeMessage(payload.explanation || ""),
          actionId: payload.actionId || null,
        });
        if (payload.status === "valid" && payload.packet) {
          setPackets((current) => ({ ...current, [payload.actionId]: payload.packet }));
        }
        // The 72h token has served its purpose — keep it out of history.
        window.history.replaceState({}, "", window.location.pathname);
      } catch {
        setLinkStatus({
          status: "invalid",
          label: "This link could not be verified",
          detail: "Open your approvals below — anything needing you is listed.",
        });
      }
    })();
  }, [location.search]);

  const loadPacket = useCallback(async (action) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/action-center/actions/${action.id}/packet`,
        { headers: authHeaders(), cache: "no-store" }
      );
      if (!response.ok) throw new Error("Packet unavailable");
      const packet = await response.json();
      setPackets((current) => ({ ...current, [action.id]: packet }));
    } catch {
      setPackets((current) => ({
        ...current,
        [action.id]: { unavailable: true },
      }));
    }
  }, []);

  const decide = useCallback(
    async (action, decision, decisionPayload = null) => {
      setBusyId(action.id);
      setOutcome({ actionId: action.id, tone: "info", label: "Applying your decision…" });
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/action-center/actions/${action.id}/decide`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", ...authHeaders() },
            body: JSON.stringify({
              decision,
              expectedState: action.state,
              ...(decisionPayload ? { decisionPayload } : {}),
            }),
          }
        );
        const payload = await response.json();
        if (!response.ok && response.status !== 409) {
          throw new Error(payload?.error || "Decision failed");
        }
        const interpreted = interpretDecideResponse(payload);
        setOutcome({
          actionId: action.id,
          tone: interpreted.tone,
          label: interpreted.label,
          detail: clientSafeMessage(interpreted.detail),
        });
        setError("");
        setChangesFor(null);
        await loadSummary();
      } catch (decideError) {
        await loadSummary();
        setOutcome(null);
        setError(clientSafeMessage(decideError?.message || "Decision failed."));
      } finally {
        setBusyId(null);
      }
    },
    [loadSummary]
  );

  const submitRequestChanges = useCallback(
    (action) => {
      if (!changeForm.change.trim()) {
        setError("Describe the change you need before sending.");
        return;
      }
      decide(action, "request_changes", {
        requestedChanges: [
          {
            section: changeForm.section,
            change: changeForm.change.trim(),
            reason: changeForm.reason.trim() || null,
            blocking: changeForm.blocking,
          },
        ],
      });
    },
    [changeForm, decide]
  );

  if (!ACTION_CENTER_BACKEND_MOUNTED) {
    return (
      <div className="client-approvals">
        <h1>Approvals</h1>
        <p>{ACTION_CENTER_DISABLED_NOTE}</p>
      </div>
    );
  }
  if (error && !summary) {
    return (
      <div className="client-approvals">
        <h1>Approvals</h1>
        <p className="client-approvals-error">{error}</p>
        <button type="button" onClick={loadSummary}>
          Retry
        </button>
      </div>
    );
  }
  if (!summary) {
    return (
      <div className="client-approvals">
        <h1>Approvals</h1>
        <p>Checking what needs you…</p>
      </div>
    );
  }

  return (
    <div className="client-approvals">
      <h1>Approvals</h1>
      {linkStatus && linkStatus.status !== "valid" ? (
        <div className="client-approvals-link-status">
          <strong>{linkStatus.label}.</strong> {linkStatus.detail}
        </div>
      ) : null}

      {linkStatus &&
      linkStatus.status === "valid" &&
      linkStatus.actionId &&
      packets[linkStatus.actionId] &&
      !(summary.actions || []).some((entry) => entry.id === linkStatus.actionId) ? (
        // The emailed decision is valid but not in the visible list (e.g.
        // beyond the page window) — render it directly so the email's
        // promise is always kept.
        <div className="client-approvals-item client-approvals-item--linked">
          <div className="client-approvals-reason">
            <strong>{packets[linkStatus.actionId].reason || "Your emailed approval"}</strong>
          </div>
          <div className="client-approvals-actions">
            {(packets[linkStatus.actionId].permittedDecisions || [])
              .filter((decision) => decision !== "request_changes")
              .map((decision) => (
                <button
                  key={decision}
                  type="button"
                  className={decision === "approve" ? "client-approvals-approve" : ""}
                  onClick={() =>
                    decide(
                      {
                        id: linkStatus.actionId,
                        state: packets[linkStatus.actionId].actionState,
                      },
                      decision
                    )
                  }
                >
                  {decisionLabel(decision)}
                </button>
              ))}
          </div>
        </div>
      ) : null}

      {summary.zeroActionState ? (
        <div className="client-approvals--zero">
          <h2>You're all caught up.</h2>
          <p>Nothing needs your approval. We'll let you know when something does.</p>
        </div>
      ) : (
        <>
          <p className="client-approvals-count">
            We need your approval on {summary.actionCount} item
            {summary.actionCount === 1 ? "" : "s"}.
          </p>
          {error ? <p className="client-approvals-error">{error}</p> : null}
          <ul className="client-approvals-list">
            {summary.actions.map((action) => {
              const packet = packets[action.id];
              const decisions = action.permittedDecisions || packet?.permittedDecisions || [];
              return (
                <li key={action.id} className="client-approvals-item">
                  <div className="client-approvals-reason">
                    <strong>{action.reason}</strong>
                    {action.estimatedSeconds ? (
                      <span className="client-approvals-estimate">
                        {formatEstimate(action.estimatedSeconds)}
                      </span>
                    ) : null}
                  </div>

                  {packet && !packet.unavailable ? (
                    <div className="client-approvals-packet">
                      {packet.campaign ? (
                        <>
                          <div><span>Campaign</span> {packet.campaign.name}</div>
                          <div><span>Goal</span> {packet.campaign.objective || "—"}</div>
                          <div>
                            <span>Budget</span>{" "}
                            {packet.campaign.budget != null
                              ? `$${Number(packet.campaign.budget).toLocaleString()}`
                              : "—"}
                          </div>
                          <div>
                            <span>Dates</span> {packet.campaign.flight?.start} →{" "}
                            {packet.campaign.flight?.end}
                          </div>
                          <div><span>Channels</span> {(packet.campaign.channels || []).join(", ") || "—"}</div>
                        </>
                      ) : null}
                      <p className="client-approvals-note">
                        Approving lets your team prepare the launch — nothing goes live
                        without the separate launch step.
                      </p>
                    </div>
                  ) : null}

                  {outcome && outcome.actionId === action.id ? (
                    <p className={`client-approvals-outcome client-approvals-outcome--${outcome.tone}`}>
                      <strong>{outcome.label}</strong>
                      {outcome.detail ? ` — ${outcome.detail}` : ""}
                    </p>
                  ) : null}

                  {changesFor === action.id ? (
                    <div className="client-approvals-changes">
                      <label>
                        What area?
                        <select
                          value={changeForm.section}
                          onChange={(event) =>
                            setChangeForm((current) => ({ ...current, section: event.target.value }))
                          }
                        >
                          {CHANGE_SECTIONS.map((section) => (
                            <option key={section}>{section}</option>
                          ))}
                        </select>
                      </label>
                      <label>
                        What should change?
                        <textarea
                          value={changeForm.change}
                          onChange={(event) =>
                            setChangeForm((current) => ({ ...current, change: event.target.value }))
                          }
                          placeholder="e.g. Lower the first-week budget"
                        />
                      </label>
                      <label>
                        Why? (optional)
                        <textarea
                          value={changeForm.reason}
                          onChange={(event) =>
                            setChangeForm((current) => ({ ...current, reason: event.target.value }))
                          }
                        />
                      </label>
                      <label className="client-approvals-blocking">
                        <input
                          type="checkbox"
                          checked={changeForm.blocking}
                          onChange={(event) =>
                            setChangeForm((current) => ({ ...current, blocking: event.target.checked }))
                          }
                        />
                        Hold the campaign until this is addressed
                      </label>
                      <div className="client-approvals-actions">
                        <button type="button" onClick={() => setChangesFor(null)}>
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="client-approvals-approve"
                          disabled={busyId === action.id}
                          onClick={() => submitRequestChanges(action)}
                        >
                          Send change request
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="client-approvals-actions">
                      <button type="button" onClick={() => loadPacket(action)}>
                        Review details
                      </button>
                      {decisions
                        .filter((decision) => decision !== "request_changes")
                        .map((decision) => (
                          <button
                            key={decision}
                            type="button"
                            className={decision === "approve" ? "client-approvals-approve" : ""}
                            disabled={busyId === action.id}
                            onClick={() => decide(action, decision)}
                          >
                            {decisionLabel(decision)}
                            {decision === "approve" ? " →" : ""}
                          </button>
                        ))}
                      {decisions.includes("request_changes") ? (
                        <button
                          type="button"
                          disabled={busyId === action.id}
                          onClick={() => setChangesFor(action.id)}
                        >
                          Request changes
                        </button>
                      ) : null}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
